// packages/auth/src/scope.ts
import type { Cents } from '@prs2/contracts'
import type { Role } from './role.js'

export type BillType = 'NEW' | 'UPWARD_ADJUSTMENT' | 'DOWNWARD_ADJUSTMENT'
export type GrantBillType = BillType | 'ALL'
/**
 * ⚠ `COMPUTE` and `COMPUTE_RECOVERY` are the SAME endpoint and two materially
 * different operations. `POST /api/bills/:reference/compute` chooses between
 * them from the bill's own state (`computeMode` in
 * `services/gateway/src/routes/bills.ts`), and they carry different authority
 * because they are different acts — see {@link REQUIRED_LEVEL} and
 * {@link MONETARY_LIMIT_APPLIES}.
 */
export type Action =
  | 'READ'
  | 'COMPUTE'
  | 'COMPUTE_RECOVERY'
  | 'APPROVE_L1'
  | 'APPROVE_L2'
  | 'REJECT'
  /**
   * ★★ WAVE 10 — pause a bill pending information from the HSP (Annex A 37/38).
   *
   * ⚠ **Its own member rather than a flavour of `REJECT`.** A rejection DISPOSES
   * of a bill and owes the HSP a reason; a pause disposes of nothing and owes
   * them a question. `REQUIRED_LEVEL` gives them the same level and
   * {@link MONETARY_LIMIT_APPLIES} deliberately does not — see both.
   */
  | 'REQUEST_INFO'
  /**
   * ★★ WAVE 10 — THE RETURN PATH. Put a paused bill back into the queue it came
   * from (`app.bill_transition.resume_status`, 060).
   *
   * ⚠ **This member is the reason `REQUEST_INFO` could be built at all.** Wave 9
   * declined to build the pause without it, because a state nothing leaves is a
   * trap — see `docs/HANDOFF.md` §W9.4. They are one feature in two verbs and
   * neither should ever exist alone.
   */
  | 'RESUME'
  /**
   * ★★ WAVE 10 — ask a named colleague to look at a bill (Annex A 37/38).
   *
   * ⚠ **It changes NO status and commits nothing**, which is what sets its
   * ceiling answer — see {@link MONETARY_LIMIT_APPLIES}. It is still a level 1
   * action: a referral is recorded in the append-only ledger against a named
   * individual, so it is not a thing a reader with no approval authority should
   * be able to write.
   */
  | 'REFER'
  /**
   * Release an already-approved bill to a Payment Agency in a settlement file
   * (Annex A story 5).
   *
   * ⚠ **It COPIES a figure; it does not DETERMINE one**, and that distinction is
   * what sets its level — see {@link REQUIRED_LEVEL}. Filed as A-19 because a
   * reviewer taking the stricter reading would be defensible.
   */
  | 'EMIT_SETTLEMENT'
  /**
   * ★ Create a bill from one record of an inbound file — Annex A story 2.
   *
   * ⚠ **A FILE IS NOT A BLANKET PERMISSION.** The intake pipeline establishes,
   * once per file, that the ingesting identity holds *some* grant for the
   * counterparty the CHANNEL resolved to. That is a different sentence from "may
   * this identity create this bill": one delivery legitimately carries new bills
   * and adjustments together (A-14 — P2C1 §12 names exactly one HSP input file),
   * and a grant scoped to one bill type covers only its own. So this is asked
   * again, per record, with the bill type the record's own adjustment indicator
   * decides.
   */
  | 'INGEST_BILL'
  /**
   * ★ Apply an upward or downward adjustment carried by an inbound file —
   * Annex A stories 3 and 4.
   *
   * ⚠ **Its own member rather than a flavour of `INGEST_BILL`**, for the same
   * reason `COMPUTE` and `COMPUTE_RECOVERY` are two members rather than one: an
   * adjustment moves money on a bill that has already been computed and may
   * already have been approved, emitted and paid, whereas a new bill commits
   * nothing until somebody approves it. One member covering both would make the
   * two axes below un-tightenable for one of them without also tightening the
   * other, and it would leave "who ingested adjustments, and against whose
   * bills" a question the audit trail cannot answer — the audit `action` is
   * taken from the same word.
   */
  | 'INGEST_ADJUSTMENT'
  /**
   * ★ Apply a Payment Agency's report about money that has already moved —
   * Annex A story 7.
   *
   * ⚠ **It records a FACT; it does not take a decision, and it does not move
   * money.** The Payment Agency has already paid the provider by the time the
   * return file arrives. PRS's answer to this record changes nothing about what
   * the Authority owes or has committed to — it changes what PRS can say about
   * what happened. That is why it sits at level 0 beside the two intakes rather
   * than at level 2 beside `COMPUTE_RECOVERY`, which DETERMINES a figure nothing
   * later reviews.
   *
   * ⚠ **The axis that actually binds here is AGENCY, and it binds through the
   * scope predicate in SQL rather than through this call.** A payment record
   * names a bill belonging to a Healthcare Service Provider while arriving on a
   * Payment Agency's channel, so — unlike `INGEST_BILL` — the agency the grant
   * model keys on is not a field of the record and cannot be known before the
   * bill is resolved. The consequence is stated at the call site: the resolution
   * is scope-filtered in SQL, this check runs after it, and both refusals carry
   * the SAME outward kind so that the pair is not an enumeration oracle.
   */
  | 'APPLY_PAYMENT_STATUS'

export interface Grant {
  readonly agencyCode: string
  readonly billType: GrantBillType
  readonly approvalLevel: 0 | 1 | 2
  /** `null` means no monetary ceiling. Never a sentinel — a sentinel gets compared. */
  readonly approvalLimit: Cents | null
}

export interface Scope {
  readonly role: Role
  readonly grants: readonly Grant[]
  /**
   * ★★★ WAVE 27 — THE AGENCY THIS OFFICER BELONGS TO, resolved at sign-in from
   * `app.app_user.agency_code`.
   *
   * ⚠ **IT IS NOT A GRANT AND MUST NOT BE TREATED AS ONE.** A grant says *"you may
   * act on this agency's bills"*. This says *"you work here"*, and the two answer
   * different questions — which is the whole of **S-100**. The seeded Recovery
   * Agency officer belongs to CPF and holds grants on SGH and NUH; the one artefact
   * addressed to CPF was therefore readable by a PSD officer and not by her.
   *
   * ⚠ **IT WIDENS EXACTLY ONE THING: artefacts ADDRESSED TO YOUR OWN AGENCY.**
   * `outboundFileScopePredicate` admits it; `billScopePredicate` and
   * `fileScopePredicate` do NOT. An officer's home agency does not make another
   * agency's bills readable, and a future predicate that reached for this field to
   * widen a bill read would be reintroducing the defect it was added to fix.
   *
   * `null` for an identity with no agency on its record — the machine identity is
   * the case that exists today — and a null never matches anything.
   *
   * ⚠ **OPTIONAL IN THE TYPE, MANDATORY ON THE SIGN-IN PATH, and the split is a
   * deliberate trade rather than laziness.** Making it required broke 138 test
   * fixtures whose only change would have been `homeAgency: null` — pure churn, and
   * churn in authorisation fixtures is where a real assertion goes to hide.
   *
   * The safety does not rest on the type. It rests on two things that are
   * checkable: the field FAILS CLOSED (absent or null matches no counterparty, so
   * a caller that forgets it sees less, never more), and
   * `identity.test.ts` asserts that the real sign-in path ALWAYS populates it —
   * which is the guarantee that actually matters, and one a required property
   * would not have given.
   */
  readonly homeAgency?: string | null
}

/**
 * ★★ *"This scope is a party to nothing, whatever the question is."*
 *
 * ══ ⚠ THIS FUNCTION MOVED HERE ON 2026-08-20, AND THE MOVE IS THE POINT ══
 *
 * It lived in `@prs2/persistence` (`scope-predicate.ts`), beside the SQL builders
 * whose empty-grant branch it answers, and that module still re-exports it so
 * every existing caller is unchanged. **It is not a persistence concern.** It
 * reads one field of {@link Scope}, builds no SQL and touches no connection —
 * and `packages/architecture`'s own register says `@prs2/auth` *"owns: Session,
 * role and scope checks. One implementation only."*
 *
 * The move was forced by a constraint rather than by tidiness. Wave 8 Task 8 puts
 * the outcome-file control on a SCREEN, and a control the server will refuse
 * outright should not be offered — the rule the navigation rail already follows,
 * which is that a client filters on **the same exported function the route's own
 * guard calls**, never on a copy of it (`components/Shell.tsx`, and
 * `lib/authority.ts`'s header on why a copy is refused on principle).
 * `routes/outcome-files.ts` guards its identity-level floor with this predicate.
 * `@prs2/persistence` builds SQL over `pg`, so a browser cannot import it and
 * must not — which left exactly three ways to satisfy the rail's rule: pull `pg`
 * into a bundle, write a THIRD copy of one sentence, or move the sentence into
 * the package that already owns scope questions and that the gateway and the
 * browser both already depend on. This is the third.
 *
 * ⚠ **Exported so a caller can ASK the question instead of re-deriving the
 * answer**, which is the S-011 shape `scope-predicate.ts`'s own comments condemn
 * three times over. `routes/outcome-files.ts` used to test
 * `scope.grants.length === 0` itself, to refuse a party-to-nothing identity one
 * layer early — behaviourally identical to `fileScopePredicate`'s own empty-grant
 * branch (`{ sql: 'false' }`) and kept in step by nobody. A route, or a screen,
 * that wants to answer before a transaction opens is a legitimate caller; a
 * second statement of the rule is not.
 *
 * ⚠ **Deliberately keyed on GRANTS ALONE, exactly as `fileScopePredicate` and
 * `billScopePredicate` are** — not on `approvalLevel`, not on `billType`. It
 * answers the identity-level question ("is this scope a party to any counterparty
 * at all") and nothing narrower: a scope WITH grants can still be refused by any
 * individual predicate, and this function does not claim otherwise.
 * `referenceScopePredicate` is the ONE predicate in that module which admits an
 * empty scope (common configuration is owned by nobody), so a caller reading that
 * table must not consult this.
 *
 * ⚠ **And a `false` here licenses nothing.** A screen may use it to decide what
 * to OFFER; the server decides what happens. Every route still asks its own
 * question against the session's real scope, and where a client shows a control
 * the route then refuses, the route's own reason is what renders.
 *
 * ⚠ Not folded into {@link authorise}'s own first branch, which spells the same
 * condition out a few dozen lines below. The two agree today and the agreement is
 * not accidental — a scope party to nothing can be authorised for nothing — but
 * `authorise` answers "may this identity do this to this bill", and unifying the
 * conditions would assert the two questions can never diverge, which is a claim
 * this change did not need to make and cannot check.
 */
export function scopeAdmitsNothing(scope: Scope): boolean {
  return scope.grants.length === 0
}

/** The bill facts an authorisation decision needs. Nothing more — this is not the bill. */
export interface ScopedBill {
  readonly reference: string
  readonly agencyCode: string
  readonly billType: BillType
  readonly amount: Cents
}

export type Decision =
  // `reason` is declared (as always-undefined) rather than absent: the test suite
  // reads `decision.reason` after narrowing on `.allowed`, and under this repo's
  // strict discriminant narrowing an absent field is a compile error, not
  // `undefined`. Declaring it keeps "an allowed decision never carries a reason"
  // true at runtime while making that absence checkable at the type level too.
  | { readonly allowed: true; readonly reason?: undefined }
  | { readonly allowed: false; readonly reason: string }

/**
 * The approval level each action requires.
 *
 * ⚠ A table, not a ternary chain, and the difference is the whole finding. The
 * previous form read `action === 'APPROVE_L2' ? 2 : action === 'APPROVE_L1' ? 1 : 0`,
 * which silently gave every other action — including REJECT, and including any
 * unrecognised string arriving from an unvalidated request body — level 0 and no
 * monetary limit. An authorisation check whose default branch grants access is the
 * exact failure this function's docstring claims it prevents.
 *
 * REJECT requires level 1 deliberately: refusing a bill is a decision with the same
 * authority as approving it, and it is not a read.
 *
 * COMPUTE requires level 0 — see {@link MONETARY_LIMIT_APPLIES} below for the full
 * reasoning, including what the monetary axis means for it.
 *
 * ══ COMPUTE_RECOVERY REQUIRES LEVEL 2, AND THE CEILING BINDS IT ══
 *
 * The compute endpoint serves two operations, and until this split they shared
 * one answer. The normal path pro-rates a bill nothing has decided yet. The
 * RECOVERY path (C3, `RECOVERABLE_STATUSES` in
 * `packages/persistence/src/bill-repository.ts`) writes the money onto a bill
 * that has **already been decided** — `AWAITING_L2` (a level 1 approval is
 * recorded against it) or `APPROVED` (a level 2 approval is). Proven live
 * before this existed: an identity holding approval level 0 and no monetary
 * limit wrote a $630 subsidy onto a bill already at `APPROVED`.
 *
 * That is not a correctness hole — a recovery and an approval can never race,
 * because recovery matches only `applied_rule_id IS NULL` and approval requires
 * `applied_rule_id IS NOT NULL`, so the two predicates are disjoint by
 * construction. It is an AUTHORITY hole, and authority is set by the most
 * consequential thing the action can do, never by the least.
 *
 * **Level 2**, because on the worst case in `RECOVERABLE_STATUSES` — `APPROVED`
 * — there is no decision left to take. Annex A story 38 puts final approval at
 * level 2; after it, nothing in this system re-examines the bill, and the
 * subsidy figure this write determines is the figure that reaches the Payment
 * Agency. Determining the money behind a decision that has already been finally
 * approved cannot require less authority than making that decision did. On the
 * milder case (`AWAITING_L2`) a level 2 approver still has to decide the bill
 * afterwards and will see the split — so the level 2 approver who is the very
 * next decision-maker on that bill is exactly who can repair it, and nobody is
 * stranded by the stricter of the two answers.
 *
 * **And the monetary ceiling BINDS, which is the opposite of the normal path's
 * answer for a reason that is stated below and inverts here.** `COMPUTE` is
 * exempt because "computing commits the government to nothing" — an approval
 * still has to follow, and story 37 escalates an over-limit bill rather than
 * freezing it. On the recovery path the approval has ALREADY happened. The
 * compute IS the commitment, so the ceiling's own question — "may this person
 * commit the government to this amount" — is exactly the question being asked.
 * A ceiling refusal here is also not the terminal trap C3 closed: the bill is
 * not blocked from progressing (it is already decided), and story 38's own
 * escalation applies — an approver whose limit reaches the amount performs the
 * repair. The route names that way out in its 403.
 *
 * ⚠ **Exposure today is zero, and it stops being zero on a known date.** A
 * clean seed has no decided-and-uncomputed row, and no API path can create one
 * (that is what C3's `applied_rule_id IS NOT NULL` guard means). The population
 * arrives with P2C1 §15.2, which requires migration to carry "inflight
 * transaction, long running traction" out of PRS 1.0 — bills mid-lifecycle,
 * some of them decided under rules this system never applied. §15.31 then
 * requires "an audit log to track the corrections of content and data
 * migrated", which is precisely what the route's `BILL_PRORATION_RECOVERY`
 * event is. The level was chosen against those clauses, before that data
 * exists, rather than after someone repairs a migrated bill they should not
 * have been able to touch.
 *
 * ══ EMIT_SETTLEMENT REQUIRES LEVEL 0, AND THE CEILING DOES NOT BIND ══
 *
 * ⚠ The close call of this table, and it is recorded as A-19 with both readings
 * because a reviewer disagreeing with it would be reasonable.
 *
 * **The distinguishing test is DETERMINE versus COPY.** `COMPUTE_RECOVERY`
 * DETERMINES a figure that no later decision reviews, so it carries the full
 * authority of the decision it substitutes for. `EMIT_SETTLEMENT` transmits a
 * figure that has ALREADY been reviewed: the authority question — *may this
 * identity commit the government to this amount* — was asked and answered at
 * `APPROVE_L2`, and emission can add nothing to it and take nothing away. A
 * second authority check over a decision already taken does not make the money
 * safer; it makes the batch identity need unlimited level 2 grants for every
 * agency, which is a far broader grant than the operation needs and a much
 * larger thing to lose.
 *
 * **What actually guards emission is a STATE PREDICATE IN SQL**, not a level:
 * `status = 'APPROVED' AND <every provenance column IS NOT NULL> AND
 * settled_at IS NULL`, in the same statement that stamps `settled_at`. That is
 * checked by the database on every row, cannot be forgotten by a caller, and is
 * what makes "emit each bill exactly once" true — see
 * `packages/persistence/src/outbound-file-repository.ts`.
 *
 * **The alternative, stated:** level 2 with the ceiling binding, by analogy with
 * `COMPUTE_RECOVERY`. Take it if an Authority answer says releasing an approved
 * bill to a Payment Agency is itself an act of authority. The cost is the broad
 * grant above; the benefit is a second human gate on money leaving. Nothing in
 * the received pack decides it, which is why it is filed rather than assumed.
 *
 * ⚠ The two axes are read back out of these tables by {@link requiredApprovalLevel}
 * and {@link monetaryLimitApplies} so the emission SQL narrows itself if this
 * entry ever changes. A second copy of "what level does emission need" living in
 * a repository is exactly the S-011 shape.
 *
 * ══ INGEST_BILL AND INGEST_ADJUSTMENT BOTH REQUIRE LEVEL 0 ══
 *
 * Annex A story 2 puts bill SUBMISSION at the Healthcare Service Provider, and
 * every HSP identity in this system holds approval level 0. Requiring more would
 * make the system's own primary intake path unauthorised for the only parties
 * that use it — the same argument `COMPUTE` already carries, and for the same
 * reason: an intake decides nothing. The bill it creates still has to be
 * approved at level 1 and again at level 2 before a cent reaches a Payment
 * Agency, and those two decisions are where the authority question lives.
 *
 * ⚠ **`INGEST_ADJUSTMENT` is the one worth arguing about, and it stays at 0.**
 * An adjustment does move money on a bill that may already be `APPROVED`, which
 * is the shape that made `COMPUTE_RECOVERY` level 2. The difference is that a
 * recovery compute is the LAST word — nothing reviews it, and the figure it
 * writes is the figure that reaches settlement — whereas an adjustment is a
 * SUBMISSION by the provider, exactly as the original bill was, and Annex A
 * stories 3 and 4 both end with it being approved. If the approval of an
 * adjustment is ever removed, this entry must move with it; it is recorded here
 * so that dependency is visible rather than discovered.
 *
 * ⚠ And the honest limit: level 0 with no ceiling means the ONLY axes that bind
 * an intake are agency and bill type. That is deliberate, and it is why the
 * intake refuses at other layers instead — an unknown officer, no entitlement on
 * the date of medical service, no rule in force, a target bill that is not the
 * submitter's. Authorisation is not doing the work here and must not be assumed
 * to be.
 *
 * ══ APPLY_PAYMENT_STATUS REQUIRES LEVEL 0 ══
 *
 * A Payment Agency reporting that it has paid a provider is stating a fact about
 * money that moved before PRS heard about it. Nothing about the record commits
 * the Authority to anything: the commitment was made at `APPROVE_L2` and
 * transmitted at `EMIT_SETTLEMENT`, and by the time this arrives the payment has
 * already happened whether PRS records it or not. Requiring an approval level in
 * order to write down what a counterparty did would make the recording of true
 * history a privilege, and the population it silently excluded would be exactly
 * the one an operator needs when a provider says it was never paid.
 *
 * ⚠ And the honest limit, which is WIDER here than for the two intakes: the
 * agency axis binds through `billScopePredicate` in SQL and not through this
 * table, because the agency is a property of the bill the record NAMES rather
 * than of the channel it arrived on. What binds through this call is the bill
 * type and the level. Whether a given Payment Agency should hold a grant over a
 * given provider's bills at all is CONFIGURATION that the received pack does not
 * furnish — filed as Q-30.
 */
const REQUIRED_LEVEL: Readonly<Record<Action, 0 | 1 | 2>> = {
  READ: 0,
  COMPUTE: 0,
  COMPUTE_RECOVERY: 2,
  APPROVE_L1: 1,
  APPROVE_L2: 2,
  REJECT: 1,
  // ★★ WAVE 10 — all three at level 1, the same as REJECT, and for the same
  // sentence: an officer who may stop a bill's progress may pause it, restart it,
  // or ask a colleague about it. Level 0 was considered for REFER and rejected —
  // a referral names an individual in an append-only ledger, and a reader with no
  // approval authority should not be able to write one.
  //
  // ⚠ RESUME is level 1 EVEN FOR A BILL PAUSED OUT OF AWAITING_L2. Resuming does
  // not approve anything: it returns the bill to the queue it was already in, for
  // whoever holds that queue's authority to decide. Requiring level 2 to undo a
  // level-1 officer's own pause would let one officer create a state they cannot
  // leave — the trap this whole pair exists to avoid, rebuilt one door along.
  REQUEST_INFO: 1,
  RESUME: 1,
  REFER: 1,
  EMIT_SETTLEMENT: 0,
  INGEST_BILL: 0,
  INGEST_ADJUSTMENT: 0,
  APPLY_PAYMENT_STATUS: 0,
}

/**
 * ★ Every action {@link authorise} can decide, at runtime.
 *
 * ⚠ DERIVED from {@link REQUIRED_LEVEL}'s own keys rather than written out a
 * second time. Anything that needs to enumerate actions — the access-control
 * matrix probes every one of them to decide what a grant actually permits — gets
 * the same list `authorise` dispatches on, so an action added to the table is
 * probed without an edit anywhere else. A hand-written list here would go stale
 * exactly the way the matrix's three hand-picked capabilities did.
 *
 * Sorted, because order stability is a requirement of this product's reports.
 */
export const ACTIONS: readonly Action[] = (Object.keys(REQUIRED_LEVEL) as Action[]).sort()

/**
 * The approval level an action requires — the same table {@link authorise} uses,
 * never a second copy.
 *
 * ⚠ Exported for ONE caller shape: a set-based operation that has to express the
 * level axis as a `WHERE` clause rather than as a per-row call. The settlement
 * emitter selects across thousands of bills in a single statement, so it cannot
 * call `authorise()` per bill *before* the statement runs — and calling it after
 * would be checking a permission for a write that has already happened. It
 * therefore narrows the grants it builds its SQL predicate from, using this.
 *
 * Anything that has a bill in hand must call {@link authorise} instead. This is
 * not a lighter-weight authorisation check; it is one axis of one.
 */
export function requiredApprovalLevel(action: Action): 0 | 1 | 2 {
  const level = REQUIRED_LEVEL[action]
  // Reached only when a caller bypasses the type. Level 2 — the most
  // restrictive answer — is the only safe response to a question we do not
  // understand, exactly as `authorise` denies rather than allows.
  return level ?? 2
}

/**
 * Whether the grant's monetary ceiling binds this action.
 *
 * ⚠ C1: `POST /api/bills/:reference/compute` never called `authorise()` at all.
 * There was no `COMPUTE` member of {@link Action} and no `REQUIRED_LEVEL` entry,
 * so the axis was ABSENT, not mis-set. Proven live before this existed:
 * `hsp.officer` (approval level 0) was refused `/approve` with
 * `APPROVE_L1 requires approval level 1` and then got **200 on `/compute` for
 * that same bill**, rewriting its `computed_at`; and `ministry.hr` (limit
 * $3,000) was refused a $6,200 bill on the monetary axis and then computed that
 * exact bill successfully. Compute is not a read — it writes six columns to
 * `app.bill`.
 *
 * **What level compute requires, and why: 0.** Annex A story 37 scopes an
 * approver by four axes — "approval authority limits, department restrictions,
 * vendor categories, and bill types". Computing a split is none of those. It is
 * a mechanical application of the pro-ration rule set PSD has already published
 * (stories 18–23: "the rule engine AUTOMATICALLY applies the appropriate
 * scenario rules"), keyed on the officer's entitlement as at the date of medical
 * service. It decides nothing: the same bill computes to the same money for
 * every caller, because subsidy is a pure function of (amount, service date,
 * entitlement, rule version) and of nothing about who asked. Story 2 puts the
 * computation at bill SUBMISSION — an HSP action, and every HSP identity holds
 * level 0 — so requiring an approval level for it would make the system's own
 * primary path unauthorised.
 *
 * **What the monetary axis means for compute: it must NOT bind, and this is the
 * load-bearing decision.** A ceiling is an *approval authority* limit. Story 37
 * says a bill exceeding it is "automatically routed to level 2 approval" — it is
 * escalated, never rendered untouchable. Binding the ceiling to compute would
 * recreate, at a different door, exactly the terminal trap C3 exists to close:
 * a $6,200 bill in an agency whose only grants cap at $3,000 could not be
 * computed by anyone, and a bill with no split cannot be approved (C3 now
 * requires `applied_rule_id IS NOT NULL` in SQL on both approvals), so it could
 * never be escalated to the level 2 approver who *is* allowed to decide it.
 * The bill would be permanently stuck with money owed and no path to determine
 * it. Refusing to compute a bill because it is expensive also answers the wrong
 * question: the ceiling asks "may this person commit the government to this
 * amount", and computing commits the government to nothing.
 *
 * ⚠ **That last sentence is true of `COMPUTE` and FALSE of
 * `COMPUTE_RECOVERY`**, which is why they are two actions rather than one. On
 * the recovery path the approval has already been recorded, so no later
 * decision stands between the split and the settlement file: computing IS
 * committing. The ceiling therefore binds recovery. The full argument, and the
 * level chosen for it, are in {@link REQUIRED_LEVEL} above.
 *
 * **What still binds.** Agency and bill type do, through the same `matching`
 * filter every action uses — `hsp.officer` computing a bill in an agency outside
 * its grant is refused here, and refused a second time in SQL by
 * `PostgresBillRepository`'s scope predicate, which is what makes it a 404
 * indistinguishable from a nonexistent bill rather than a 403 enumeration
 * oracle. A scope with no grants at all is refused before any of this.
 *
 * A table rather than `action === 'READ' || action === 'COMPUTE'`, for the same
 * reason {@link REQUIRED_LEVEL} is one: `Record<Action, boolean>` makes the
 * compiler demand an entry for every future action, so adding one cannot
 * silently inherit "unlimited" from a disjunction nobody updated. An action that
 * bypasses the type entirely reads `undefined` here and is treated as LIMITED —
 * the restrictive answer, never the permissive one.
 */
const MONETARY_LIMIT_APPLIES: Readonly<Record<Action, boolean>> = {
  READ: false,
  COMPUTE: false,
  COMPUTE_RECOVERY: true,
  APPROVE_L1: true,
  APPROVE_L2: true,
  REJECT: true,

  // ★★ WAVE 10 — `false` for all three, and this is a decision, not an omission.
  //
  // The ceiling asks ONE question: *may this identity commit the government to
  // this amount?* `REJECT` answers yes-it-binds because refusing a bill disposes
  // of it — the money question is settled at zero and the HSP is owed a reason.
  // None of these three disposes of anything:
  //
  //   * `REQUEST_INFO` pauses a bill. The decision is still to be made, by
  //     whoever has the authority to make it.
  //   * `RESUME` puts it back exactly where it was.
  //   * `REFER` changes no status at all.
  //
  // ⚠ AND BINDING IT WOULD BE ACTIVELY HARMFUL, which is the stronger argument.
  // A level-1 officer under the ceiling who spots something wrong on a $2M bill
  // they cannot approve is the person MOST likely to need to ask a question about
  // it — and binding the ceiling here would leave them able to do nothing but
  // watch it go past. It would also strand any over-limit bill that somebody had
  // already paused: nobody under the ceiling could resume it, which is the
  // terminal trap `EMIT_SETTLEMENT` records immediately below, rebuilt on a state
  // that a single level-1 action can create.
  //
  // ⚠ What this costs, stated rather than glossed: a level-1 officer holding no
  // monetary limit CAN pause a bill of any size. That is a delay, not a
  // disposal — it is reversible by any level-1 officer through `RESUME`, both
  // halves are in the append-only ledger with a MANDATORY comment (060), and the
  // bill's own screen names the pause. A silent, unattributable pause would be a
  // different matter; this one cannot happen.
  REQUEST_INFO: false,
  RESUME: false,
  REFER: false,

  // ⚠ `false`, for the reason spelled out in REQUIRED_LEVEL: the ceiling asks
  // "may this identity commit the government to this amount", and that question
  // was answered at APPROVE_L2. Emission copies the answer. Binding it here
  // would also strand every over-limit bill in the one state from which nothing
  // can move it — approved, computed, and never sent — which is the terminal
  // trap C3 exists to close, rebuilt at the last door instead of the first.
  EMIT_SETTLEMENT: false,

  // ⚠ `false` for BOTH, and this is the same load-bearing decision `COMPUTE`
  // carries rather than a convenience.
  //
  // Story 37 says a bill exceeding an approver's limit is "automatically routed
  // to level 2 approval" — it is ESCALATED, never rendered untouchable. Binding
  // the ceiling to an intake would mean an expensive bill could not be ingested
  // at all, and a bill that cannot be ingested can never be escalated to the
  // approver who is allowed to decide it. That is C3's terminal trap rebuilt at
  // the very first door: the money is owed either way, and refusing to record it
  // does not make it stop being owed — it only makes it invisible.
  //
  // ⚠ For `INGEST_ADJUSTMENT` the ceiling would also have nothing coherent to
  // bind ON. A record carries a signed DELTA; the resulting running total is not
  // knowable until the original has been resolved, and resolving the original
  // before answering the authorisation question is exactly what turns a refusal
  // into an enumeration oracle (the caller would learn whether a reference it
  // guessed is real). So the figure `authorise` is given for an adjustment is the
  // MAGNITUDE the record moves — see `services/transfer/src/grammars/hsp-adjustment.ts`
  // — which is the only amount the record itself carries. If a reviewer ever
  // makes this bind, that ordering problem is the first thing to solve.
  INGEST_BILL: false,
  INGEST_ADJUSTMENT: false,

  // ⚠ `false`, and here the ceiling would not merely be unhelpful — it would be
  // incoherent. A ceiling asks "may this identity commit the government to this
  // amount", and a payment record commits the government to NOTHING: the money
  // left the Payment Agency before the file was written. Binding it would mean
  // PRS refused to record an expensive payment that had already been made, which
  // does not un-make the payment; it only makes the books disagree with the bank.
  APPLY_PAYMENT_STATUS: false,
}

/**
 * Whether the grant's monetary ceiling binds this action — the same table
 * {@link authorise} uses, never a second copy.
 *
 * ⚠ Exported for the same single caller shape as {@link requiredApprovalLevel},
 * and used there as a REFUSAL rather than as a filter: a set-based predicate
 * cannot express a per-bill ceiling, so an emitter that finds this `true` must
 * stop rather than quietly emit without checking it. That turns "somebody
 * flipped the table entry and did not update the SQL" from a silent authority
 * hole into a loud failure at the call site.
 */
export function monetaryLimitApplies(action: Action): boolean {
  // `!== false`, so an action that bypassed the type reads `undefined` and is
  // treated as LIMITED — the restrictive answer, matching `authorise`.
  return MONETARY_LIMIT_APPLIES[action] !== false
}

/**
 * May this scope perform this action on this bill?
 *
 * Four axes: the grant's agency must match the bill's, the grant's bill type must
 * cover it, the grant's approval level must reach the action, and the bill's amount
 * must be within the grant's limit. Annex A story 37 scopes a Level 1 approver by
 * exactly those four — "approval authority limits, department restrictions, vendor
 * categories, and bill types" — so a role name alone can never answer this question.
 *
 * The first two always bind. The last two are per-action and declared in the two
 * tables above rather than inferred: {@link REQUIRED_LEVEL} says what level the
 * action needs, {@link MONETARY_LIMIT_APPLIES} says whether the ceiling binds it at
 * all. `READ` and `COMPUTE` are exempt from the ceiling; every action that
 * commits the Authority to a figure — `APPROVE_L1`, `APPROVE_L2`, `REJECT` and
 * `COMPUTE_RECOVERY` — is not. Read MONETARY_LIMIT_APPLIES for why the ordinary
 * compute is exempt and the recovery compute is not: both are deliberate
 * decisions with a failure mode behind them, not oversights, and they differ
 * because one is followed by an approval and the other follows one.
 *
 * **Denies by default.** An empty grant list denies; an unmatched action denies. The
 * failure mode of an authorisation check that defaults open is that nobody notices
 * until the wrong person has seen something.
 */
export function authorise(scope: Scope, action: Action, bill: ScopedBill): Decision {
  if (scope.grants.length === 0) {
    return { allowed: false, reason: `role ${scope.role} has no grants` }
  }

  const requiredLevel = REQUIRED_LEVEL[action]
  if (requiredLevel === undefined) {
    // Reached only when a caller bypasses the type — an unvalidated request body,
    // a JSON payload, a future action added to the table but not here. Denying is
    // the only safe answer to a question we do not understand.
    return { allowed: false, reason: `unknown action ${String(action)}` }
  }

  const matching = scope.grants.filter(
    (grant) =>
      grant.agencyCode === bill.agencyCode &&
      (grant.billType === 'ALL' || grant.billType === bill.billType),
  )

  if (matching.length === 0) {
    return {
      allowed: false,
      reason: `no grant covers agency ${bill.agencyCode} and bill type ${bill.billType}`,
    }
  }

  const atLevel = matching.filter((grant) => grant.approvalLevel >= requiredLevel)
  if (atLevel.length === 0) {
    return { allowed: false, reason: `${action} requires approval level ${requiredLevel}` }
  }

  // `!== true`, not `=== false`: an action that bypassed the type reads
  // `undefined` from the table, and the restrictive branch — the limit APPLIES —
  // is the only safe answer to a question we do not understand. See
  // MONETARY_LIMIT_APPLIES for why COMPUTE and READ are exempt and the three
  // decisions are not.
  if (MONETARY_LIMIT_APPLIES[action] !== true) return { allowed: true }

  const withinLimit = atLevel.some(
    (grant) => grant.approvalLimit === null || bill.amount <= grant.approvalLimit,
  )
  if (!withinLimit) {
    return {
      allowed: false,
      reason: `bill ${bill.reference} exceeds the approval limit on every matching grant`,
    }
  }

  return { allowed: true }
}

/**
 * Roles that may read the ENTIRE audit trail — every actor's events, not only
 * their own.
 *
 * P2C1 §12.142 authorises "authorised users", not every signed-in one. This
 * table is explicit and keyed on role rather than inferred from a role's bill
 * grants: inferring it (say, "has an ALL bill grant") would silently change
 * who can read the trail the next time grants are reshaped for an unrelated
 * reason, and it is the wrong shape besides — most audit rows carry
 * `resourceType: 'http'` (`audit-hook.ts` records one for every request) and
 * have no bill to check a grant against at all. Today only `PSD_USER` — the
 * Authority's own administrator role — holds this. Every other role reads
 * only the events it produced itself; see `mayReadFullAuditTrail` and
 * `PostgresAuditRepository.list`, which enforces the split in SQL.
 */
const AUDIT_TRAIL_ROLES: ReadonlySet<Role> = new Set<Role>(['PSD_USER'])

/** May this scope read every actor's audit events, not only its own? */
export function mayReadFullAuditTrail(scope: Scope): boolean {
  return AUDIT_TRAIL_ROLES.has(scope.role)
}

/**
 * Roles that may resolve entitlement for ANY officer, not only one they hold
 * an in-scope bill for.
 *
 * `GET /api/entitlement` has no bill to check a grant against — an officer's
 * entitlement period is looked up on its own, often before any bill exists
 * for them at all. Left unchecked (C1), that made every officer's scheme,
 * ward class and scheme-change date resolvable by ANY signed-in role for the
 * cost of guessing an officer id — ids that are trivially enumerable
 * (`OFF-01`..`OFF-20`) and handed out by `/api/bills` besides. All 20 seeded
 * officers leaked to a single-agency HSP session in the finding that proved
 * this.
 *
 * Keyed on role, explicit, the same shape as {@link mayReadFullAuditTrail} —
 * never inferred from how wide a role's bill grants happen to be, because
 * that silently changes who can enumerate officers the next time grants are
 * reshaped for an unrelated reason. Today only `PSD_USER` — the Authority's
 * own administrator role — holds this. Every other role may resolve
 * entitlement only for an officer it has at least one in-scope bill for,
 * checked in SQL by `PostgresBillRepository.hasBillFor` — never fetched and
 * filtered afterwards, the same discipline `scopePredicate` itself enforces.
 */
const ENTITLEMENT_ANY_OFFICER_ROLES: ReadonlySet<Role> = new Set<Role>(['PSD_USER'])

/** May this scope resolve entitlement for any officer, with no bill to check against? */
export function mayResolveAnyOfficerEntitlement(scope: Scope): boolean {
  return ENTITLEMENT_ANY_OFFICER_ROLES.has(scope.role)
}

/**
 * ★★★ Roles that may RE-DIVIDE MONEY on bills already computed — the
 * re-proration sweep (P2C1 §12, Recovery Agency row 7).
 *
 * ══ ⚠ WHY THIS EXISTS AT ALL, WHEN IT IS THE SAME ROLE SET AS THREE OTHERS ══
 *
 * **Wave 13 gated `POST /api/bills/reprorate-sweep` on
 * {@link mayReadFullAuditTrail}, and the reason it gave does not survive
 * inspection.** The route's own comment read: *"The SAME capability the audit
 * archive and the retention register gate on … reusing an existing capability
 * rather than minting a new one is the 'one place, or it will be missed in some'
 * discipline."*
 *
 * That discipline is about not deciding one question in two places. It is not a
 * licence to answer a DIFFERENT question with the same predicate. `audit-archive`
 * and `retention` reuse it defensibly — both are compliance administration over
 * the audit trail, the same resource the predicate is named for. A re-proration
 * sweep is not: it is bulk money movement in the BILLING domain, and the question
 * *"may this scope read every actor's audit events?"* has no bearing on it.
 *
 * ══ ★ THE FAILURE SCENARIO, WHICH IS AN INVITED CHANGE AND NOT AN EXOTIC ONE ══
 *
 * P2C1 §12.142 authorises *"authorised users"* to read the trail — not "the
 * administrator". The single most likely future edit to {@link mayReadFullAuditTrail}'s
 * role set is therefore adding a read-only auditor so an external auditor can do
 * what the clause contemplates. Before this predicate existed, that one-line edit
 * would ALSO have granted that role:
 *
 *   * `POST /api/bills/reprorate-sweep` — re-divide the split on every bill in
 *     the estate, in bulk, irreversibly (a re-proration is an append-only ledger
 *     row; reversing one is a further correction made by a person); and
 *   * `POST /api/reproration-alerts/:code` — address a file of officers'
 *     entitlement facts and co-payment movements to a Recovery Agency.
 *
 * Nothing in the auth package, in either route, or in this predicate's own
 * docstring would have flagged it, because the reviewer would have been reading a
 * change about audit reads. **Found by the Waves 11-14 security review, 2026-08-23,
 * and recorded as S-084.**
 *
 * ⚠ **The role set is IDENTICAL to `AUDIT_TRAIL_ROLES` today and that is the
 * point** — no behaviour changes, and the two can now move apart without one
 * dragging the other. Keyed on role and explicit, the same shape every other
 * capability in this package uses.
 */
const REPRORATION_ROLES: ReadonlySet<Role> = new Set<Role>(['PSD_USER'])

/** May this scope run the sweep that re-divides money on already-computed bills? */
export function mayReprorateBills(scope: Scope): boolean {
  return REPRORATION_ROLES.has(scope.role)
}

/**
 * ★★ Roles that may ADDRESS a re-proration alert file to a Recovery Agency.
 *
 * ⚠ **Separate from {@link mayReprorateBills}, and the separation is not
 * ceremony.** They are different acts with different blast radii: one changes
 * what PRS holds, the other DISCLOSES officers' entitlement fingerprints and
 * co-payment movements to a third party (P2C1 §1.1.3's counterparty groups). A
 * deployment could reasonably let an operations role run the sweep while
 * reserving the disclosure to the Authority, and a single predicate would make
 * that impossible to express.
 *
 * Same role set today. Same reason as above for keeping it its own set.
 */
const REPRORATION_ALERT_ROLES: ReadonlySet<Role> = new Set<Role>(['PSD_USER'])

/** May this scope produce the Recovery Agency alert file? */
export function mayEmitReprorationAlert(scope: Scope): boolean {
  return REPRORATION_ALERT_ROLES.has(scope.role)
}

/**
 * Roles that may see WHICH PERSON approved or rejected a bill — the approver's
 * user id and display name on `GET /api/bills/:reference/transitions`.
 *
 * ⚠ **P2C11 §3.13 requires user IDs to be sanitised out of anything sent to a
 * third party.** The four counterparty groups in P2C1 §1.1.3 — Entitlement
 * Agencies, Healthcare Service Providers, Payment Agencies and Recovery
 * Agencies — are counterparties, not the Authority. An HSP holding an in-scope
 * bill was receiving `actorUserId`, `actorDisplayName` and `actorRole` for
 * every government officer who had ever decided one of its bills: the stable
 * internal UUID of a named public servant, handed to an external provider, for
 * every approval in its agency. The screen only renders the name and the role,
 * which is precisely why nobody noticed the id — the API is what discloses it,
 * not the screen.
 *
 * **What an HSP actually needs** is what its own question is: *why was this
 * bill refused, and by what authority?* That is the transition kind, the level,
 * the timestamp and — for a rejection — the reason the `reject` route makes
 * mandatory. `actorRole` is kept for everyone because "a Ministry/Agency HR
 * approver decided this" is the answer to "by what authority" and identifies no
 * individual. The id and the name are removed for everyone else.
 *
 * Today: `PSD_USER` (the Authority's administrator, which already reads the
 * whole audit trail), `MINISTRY_HR_USER` (Annex A stories 37 and 38 — the level
 * 2 approver is told to "view detailed bill data including level 1 approval
 * history and comments", so this role is the one the requirement names), and
 * `PRS_SERVICE` (the machine caller that would drive a batch operation across
 * both).
 *
 * ⚠ `MBIM_USER` and `MMBA_USER` are Authority-side administrative roles and are
 * deliberately NOT here: no clause in the received pack gives either of them a
 * need for the deciding officer's identity, and widening a disclosure on the
 * strength of "they are internal" is how §3.13 gets eroded. If the Authority
 * wants them included, that is a policy decision to file, not one to assume —
 * and it is one line in this table.
 *
 * Keyed on role, explicit, the same shape as {@link mayReadFullAuditTrail} and
 * {@link mayResolveAnyOfficerEntitlement} — never inferred from how wide a
 * role's bill grants happen to be. Enforced by NOT SELECTING the columns
 * (`PostgresTransitionRepository.listForBill`), so the values never enter this
 * process's memory for a caller who may not have them, rather than being
 * fetched and then deleted.
 */
const APPROVER_IDENTITY_ROLES: ReadonlySet<Role> = new Set<Role>(['PSD_USER', 'MINISTRY_HR_USER', 'PRS_SERVICE'])

/** May this scope see which individual approved or rejected a bill? */
export function mayReadApproverIdentity(scope: Scope): boolean {
  return APPROVER_IDENTITY_ROLES.has(scope.role)
}
